import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import {
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  ExternalLink,
} from "lucide-react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import {
  ResponsiveTable,
  MobileCard,
  TableCardSkeleton,
  type Column,
} from "@/components/molecules/ResponsiveTable";
import { TableCard, TableHeader, EmptyState } from "./reports.styles";

interface ReportEntry {
  id: string;
  contactId: string;
  contactName: string;
  suburb: string;
  sentAt: string;
  status: string;
  s3Url?: string;
}

interface ReportHistoryTableProps {
  reports?: ReportEntry[];
  loading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
}

const statusConfig: Record<
  string,
  {
    icon: typeof CheckCircle;
    color: "success" | "error" | "warning";
    label: string;
  }
> = {
  sent: {
    icon: CheckCircle,
    color: "success",
    label: "Sent",
  },
  failed: {
    icon: XCircle,
    color: "error",
    label: "Failed",
  },
  pending: {
    icon: Clock,
    color: "warning",
    label: "Pending",
  },
};

function formatDate(date: string): string {
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getStatusChip(report: ReportEntry) {
  const status = statusConfig[report.status] || statusConfig.pending;
  const StatusIcon = status.icon;
  return (
    <Chip
      icon={<StatusIcon size={14} />}
      label={status.label}
      color={status.color}
      size="small"
      variant="outlined"
    />
  );
}

function getViewAction(report: ReportEntry) {
  if (!report.s3Url) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  return (
    <Tooltip title="View report">
      <IconButton
        size="small"
        href={report.s3Url}
        target="_blank"
        rel="noopener noreferrer"
        color="primary"
        aria-label="View report in new tab"
      >
        <ExternalLink size={18} />
      </IconButton>
    </Tooltip>
  );
}

export function ReportHistoryTable({
  reports,
  loading,
  onLoadMore,
  hasMore,
}: ReportHistoryTableProps) {
  const columns: Column<ReportEntry>[] = [
    {
      key: "contactName",
      header: "Contact",
      render: (report) => (
        <Typography variant="body2" fontWeight={500}>
          {report.contactName}
        </Typography>
      ),
    },
    {
      key: "suburb",
      header: "Suburb",
      render: (report) => (
        <Typography variant="body2" color="text.secondary">
          {report.suburb}
        </Typography>
      ),
    },
    {
      key: "sentAt",
      header: "Sent At",
      render: (report) => (
        <Typography variant="body2" color="text.secondary">
          {formatDate(report.sentAt)}
        </Typography>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (report) => getStatusChip(report),
    },
    {
      key: "view",
      header: "View",
      align: "center",
      render: (report) => getViewAction(report),
    },
  ];

  const mobileCardRender = (report: ReportEntry) => (
    <MobileCard
      title={report.contactName}
      subtitle={report.suburb}
      fields={[{ label: "Sent", value: formatDate(report.sentAt) }]}
      status={getStatusChip(report)}
      actions={report.s3Url ? getViewAction(report) : undefined}
    />
  );

  const header = (
    <TableHeader>
      <Typography variant="subtitle1" fontWeight={600}>
        Report History
      </Typography>
    </TableHeader>
  );

  const emptyState = (
    <EmptyState>
      <Box sx={{ color: "text.disabled", mb: 1 }}>
        <FileText size={48} />
      </Box>
      <Typography variant="subtitle2" fontWeight={600}>
        No reports yet
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Report history will appear here once reports are generated.
      </Typography>
    </EmptyState>
  );

  const footer = hasMore ? (
    <Box
      sx={{
        p: 2,
        borderTop: 1,
        borderColor: "divider",
        textAlign: "center",
      }}
    >
      <Button
        onClick={onLoadMore}
        disabled={loading}
        color="primary"
        size="small"
      >
        {loading ? "Loading..." : "Load More"}
      </Button>
    </Box>
  ) : undefined;

  return (
    <TableCard variant="outlined">
      <ResponsiveTable
        data={reports ?? []}
        columns={columns}
        mobileCardRender={mobileCardRender}
        getKey={(report) => report.id}
        emptyState={emptyState}
        loading={loading}
        loadingSkeleton={<TableCardSkeleton fieldCount={1} showStatus />}
        header={header}
        footer={footer}
      />
    </TableCard>
  );
}
